import { NativeError, RuntimeError } from '@belimo-retrofit-portal/logic';
import { SagaIterator } from 'redux-saga';
import { sentryCatch } from 'src/modules/config/utils/sentryCatch';
import { getHvacResults } from 'src/modules/hvac/selectors/getHvacResults';
import { showNotification } from 'src/modules/notifications/sagas/showNotification';
import { getProjectDetails } from 'src/modules/project-view/selectors/getProjectDetails';
import { buildReportDocument } from 'src/modules/report/sagas/utils/buildReportDocument';
import { getReportFormData } from 'src/modules/report/selectors/getReportFormData';
import { getReportGenerated } from 'src/modules/report/selectors/getReportGenerated';
import { SAVE_PROJECT } from 'src/modules/save-project/SaveProjectAction';
import { logDebug, logError } from 'src/sagas/utils/logging';
import { GetRequestActionType } from 'src/utils/createActions';
import { select, call, takeLatest, put } from 'typed-redux-saga';
import { makeRestRequest } from 'src/modules/api/sagas/utils/makeRestRequest';
import { SaveProjectData } from 'src/modules/save-project/SaveProjectData';
import { getTolgeeInstance } from 'src/modules/config/selectors/getTolgeeInstance';
import dayjs from 'dayjs';
import { blobToBase64 } from 'src/modules/save-project/blobToBase64';

export function* saveProjectSaga(
  action: GetRequestActionType<typeof SAVE_PROJECT>,
): SagaIterator<void> {
  const { email } = action.data;
  const project = yield* select(getProjectDetails);

  try {
    yield* put(SAVE_PROJECT.pending());
    const document = yield* select(getReportGenerated, project);
    const formData = yield* select(getReportFormData, project);
    const results = yield* select(getHvacResults, project);
    const tolgee = yield* select(getTolgeeInstance);

    const blob = yield* call(
      buildReportDocument,
      document,
      formData,
      project,
      { },
      results,
    );

    const pdf = yield* call(blobToBase64, blob);
    yield* call(logDebug, 'Generating report document...', {
      email: email,
      id: project.id,
      size: pdf.length,
    });

    const data: SaveProjectData = {
      email: email,
      buildingType: tolgee.t(`common/buildingType/${project.data.building.type}`),
      location: tolgee.t(`common/country/name/${project.data.building.address.country}`),
      reportDate: dayjs(document.cover.reportDate).format('DD.MM.YYYY'),
      projectId: project.id,
      pdf: pdf,
    }

    yield* call(makeRestRequest, 'POST', '/save-project', data);
    yield* call(showNotification, { variant: 'success', type: 'done', messageId: 'saveProjectForm/success' });
    yield* put(SAVE_PROJECT.success());
  } catch (error) {
    const wrapped = new RuntimeError(
      'Could not send project',
      { email: email, id: project.id },
      NativeError.wrap(error),
    );
    yield* put(SAVE_PROJECT.failure(wrapped));
    yield* call(logError, 'Saving project... error', error);

    yield* call(sentryCatch, wrapped);
    yield* call(showNotification, { variant: 'error', type: 'error' });
  }
}

export function* saveProjectRoot(): SagaIterator<void> {
  yield* takeLatest(SAVE_PROJECT.isRequest, saveProjectSaga);
}
